import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/store/ui.store";
import { ROUTES } from "@/config/routes.config";
import { authService } from "@/services/auth.service";
import { useUserRoles } from "@/hooks/useUserRoles";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal";

// ─── Nav Item Config ──────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const DashboardIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const EmployeesIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

// ─── Sub-menu icons (h-4 w-4) ─────────────────────────────────────────────────

/** Candidate: person with plus badge */
const CandidateIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M18 7.5a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM6.5 21a6.5 6.5 0 0113 0M19 8h3m-1.5-1.5v3"
    />
  </svg>
);

/** Employee List: person with list lines */
const EmployeeListIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7a4 4 0 118 0 4 4 0 01-8 0zM4 21a8 8 0 0116 0M3 4h3M3 8h2M3 12h3"
    />
  </svg>
);

/** Exit: door with arrow */
const ExitIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3-3H9m9.75 0l-3-3m3 3l-3 3"
    />
  </svg>
);

/** Org Chart: tree diagram */
const OrgChartIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4v4m0 0H7.5A2.5 2.5 0 005 10.5V13m7-5h4.5A2.5 2.5 0 0119 10.5V13M8 20H4v-4h4v4zm8 0h-4v-4h4v4zm8 0h-4v-4h4v4z"
    />
  </svg>
);

/** Employee Reporting: chart with document — unique icon for reporting */
const EmployeeReportingIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const LeaveIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

/** Leave sub-item: calendar with check — for the "Leave" dashboard link */
const LeaveCalendarIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 16l2 2 4-4" />
  </svg>
);

/** Leave Reporting: table with rows — for the reporting sub-item */
const LeaveReportingIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const DocumentsIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

/** Salary Advance: banknote */
const SalaryAdvanceIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path strokeLinecap="round" d="M6 9.5v5M18 9.5v5" />
  </svg>
);

/** Salary Advance sub-item: wallet with plus */
const SalaryAdvanceRequestIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 7a2 2 0 012-2h12a2 2 0 012 2v1M3 7v10a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2H5a2 2 0 01-2-2zm13 6h.01"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0 text-red-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const SettingsIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

// ─── My Info icon (person-cycle / ID badge) ─────────────────────────────────
const MyInfoIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

/** My Team: people group — distinct from EmployeesIcon */
const MyTeamIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    className="h-4 w-4 text-slate-400 ml-auto shrink-0 transition-transform duration-200"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    className="h-4 w-4 text-slate-400 ml-auto shrink-0 transition-transform duration-200"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
  </svg>
);

const PerformanceIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

/** Appraisal sub-item: clipboard with check */
const AppraisalIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
    />
  </svg>
);

/** Appraisal Reporting sub-item: bar chart */
const AppraisalReportingIcon = () => (
  <svg
    className="h-4 w-4 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13h2v8H3zm6-4h2v12H9zm6-3h2v15h-2zm6-2h2v17h-2z"
    />
  </svg>
);

// Nav items are now built inside the component to access t()



// ─── Shared dropdown sub-link class ───────────────────────────────────────────
const subLinkClass = (isActive: boolean) =>
  `flex items-center gap-3 pl-8 pr-3 py-2 rounded-lg text-xs font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-all ${
    isActive ? "text-white bg-white/5 font-semibold" : ""
  }`;

// ─── Sidebar Component ────────────────────────────────────────────────────────
export interface SidebarProps {
  isSalaryAdvanceVisible?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isSalaryAdvanceVisible = false,
}) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const confirmLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setLogoutError('');
    try {
      await authService.logout();
      // Only close modal on success (or let unmount handle it)
      setIsLogoutModalOpen(false);
    } catch {
      setLogoutError('Unable to sign out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };
  const { t } = useTranslation();
  const sidebarOpen = useUIStore((state) => state.sidebarOpen);
  const location = useLocation();
  const { isAdmin, isGlobalHR, isCountryHR, approvalLevel } = useUserRoles();
  const canViewHRPages = isAdmin || isGlobalHR || isCountryHR;
  // Approvals page: visible to anyone with an approvalLevel assigned on their employee record.
  // Country HR always has approvalLevel 1 but we also gate via isCountryHR for safety.
  const canSeeApprovals = isCountryHR || (approvalLevel !== null && approvalLevel !== undefined);

  // ── Active-path checks ──────────────────────────────────────────────────
  const isEmployeeActive =
    (location.pathname.startsWith("/employees") &&
      location.pathname !== ROUTES.MY_INFO) ||
    location.pathname.startsWith("/candidates") ||
    location.pathname.startsWith("/exits") ||
    location.pathname.startsWith("/org-chart");

  const isLeaveActive =
    location.pathname === ROUTES.LEAVE ||
    location.pathname.startsWith("/leave/");

  const isPerformanceActive =
    location.pathname.startsWith(ROUTES.PERFORMANCE_APPRAISALS) ||
    location.pathname === ROUTES.APPRAISAL_REPORTING;

  const isDocumentsActive = location.pathname === ROUTES.DOCUMENTS || location.pathname.startsWith(`${ROUTES.DOCUMENTS}/`);

  // ── Dropdown state ──────────────────────────────────────────────────────
  const [employeesDropdownOpen, setEmployeesDropdownOpen] = useState(isEmployeeActive);
  const [leaveDropdownOpen, setLeaveDropdownOpen] = useState(isLeaveActive);
  const [performanceDropdown, setPerformanceDropdown] = useState({
    pathname: location.pathname,
    open: isPerformanceActive,
  });
  const performanceDropdownOpen =
    performanceDropdown.pathname === location.pathname
      ? performanceDropdown.open
      : isPerformanceActive;

  const [documentsDropdown, setDocumentsDropdown] = useState({ pathname: location.pathname, open: isDocumentsActive });
  const documentsDropdownOpen = documentsDropdown.pathname === location.pathname ? documentsDropdown.open : isDocumentsActive;

  const isSalaryAdvanceActive =
    location.pathname === ROUTES.SALARY_ADVANCE ||
    location.pathname.startsWith(`${ROUTES.SALARY_ADVANCE}/`);
  const [salaryAdvanceDropdown, setSalaryAdvanceDropdown] = useState({ pathname: location.pathname, open: isSalaryAdvanceActive });
  const salaryAdvanceDropdownOpen =
    salaryAdvanceDropdown.pathname === location.pathname ? salaryAdvanceDropdown.open : isSalaryAdvanceActive;

  if (!sidebarOpen) return null;

  // ── Sub-link data ───────────────────────────────────────────────────────
  const employeeLinks = [
    { label: t("nav.candidates"), to: ROUTES.CANDIDATES, icon: <CandidateIcon /> },
    { label: t("nav.employees"), to: ROUTES.EMPLOYEES, icon: <EmployeeListIcon /> },
    { label: t("nav.orgChart"), to: ROUTES.ORG_CHART, icon: <OrgChartIcon /> },
    { label: t("nav.exits"), to: ROUTES.EXITS, icon: <ExitIcon /> },
    { label: t("nav.employeeReporting", "Employee Reporting"), to: ROUTES.EMPLOYEE_REPORTING, icon: <EmployeeReportingIcon /> },
  ];

  const leaveLinks = [
    { label: t("nav.leave", "Leave"), to: ROUTES.LEAVE, icon: <LeaveCalendarIcon /> },
    { label: t("nav.leaveReporting", "Leave Reporting"), to: ROUTES.LEAVE_REPORTING, icon: <LeaveReportingIcon /> },
  ].filter(item => item.to === ROUTES.LEAVE || canViewHRPages);

  const performanceLinks = [
    { label: t("nav.appraisal", "Appraisal"), to: ROUTES.PERFORMANCE_APPRAISALS, icon: <AppraisalIcon /> },
    { label: t("nav.appraisalReporting", "Appraisal Reporting"), to: ROUTES.APPRAISAL_REPORTING, icon: <AppraisalReportingIcon /> },
  ].filter(item => item.to === ROUTES.PERFORMANCE_APPRAISALS || canViewHRPages);

  // ── Items rendered after the dropdowns ──────────────────────────────────
  const documentLinks = [
    { label: t("nav.documents"), to: ROUTES.DOCUMENTS, icon: <DocumentsIcon /> },
    { label: t("nav.documentReporting", "Document Reporting"), to: ROUTES.DOCUMENT_REPORTING, icon: <EmployeeReportingIcon /> },
  ].filter(item => item.to === ROUTES.DOCUMENTS || canViewHRPages);
  const salaryAdvanceLinks = [
    { label: t("nav.salaryAdvance", "Salary Advance"), to: ROUTES.SALARY_ADVANCE, icon: <SalaryAdvanceRequestIcon /> },
    canSeeApprovals && { label: t("nav.salaryAdvanceApprovals", "Salary Advance Approvals"), to: ROUTES.SALARY_ADVANCE_APPROVALS, icon: <AppraisalIcon /> },
    canViewHRPages && { label: t("nav.salaryAdvanceReporting", "Salary Advance Reporting"), to: ROUTES.SALARY_ADVANCE_REPORTING, icon: <EmployeeReportingIcon /> },
  ].filter(Boolean) as NavItem[];
  const navItemsBottom: NavItem[] = [];
  if (isAdmin || isGlobalHR) {
    navItemsBottom.push({ label: t("nav.settings"), to: ROUTES.SETTINGS, icon: <SettingsIcon /> });
  }

  return (
    <aside
      className="w-[260px] flex-shrink-0 flex flex-col justify-between overflow-hidden"
      style={{ backgroundColor: "#0F2147" }}
    >
      {/* Logo */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <img src="/autochekhrms.png" alt="Talentcheck" className="h-14 w-auto" />
        </div>

        {/* Nav */}
        <nav className="mt-4 px-3 space-y-1">
          {/* Dashboard */}
          <NavLink
            to={ROUTES.DASHBOARD}
            end
            data-tour="tour-dashboard"
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "active" : ""}`
            }
          >
            <DashboardIcon />
            <span>{t("nav.dashboard")}</span>
          </NavLink>

          {/* ─── Employees Dropdown ─────────────────────────────────────────── */}
          {canViewHRPages && <div>
            <button
              onClick={() => setEmployeesDropdownOpen(!employeesDropdownOpen)}
              data-tour="tour-employees"
              className={`sidebar-link w-full text-left flex items-center ${
                isEmployeeActive ? "active" : ""
              }`}
            >
              <EmployeesIcon />
              <span className="truncate">{t("nav.employee")}</span>
              {employeesDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>

            {employeesDropdownOpen && (
              <div className="mt-1 space-y-0.5 pl-4 transition-all duration-300">
                {employeeLinks.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === ROUTES.EMPLOYEES}
                    className={({ isActive }) => subLinkClass(isActive)}
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>}

          {/* My Info link */}
          <NavLink
            to={ROUTES.MY_INFO}
            data-tour="tour-my-info"
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "active" : ""}`
            }
          >
            <MyInfoIcon />
            <span>{t("nav.myInfo")}</span>
          </NavLink>
          <NavLink
            to={ROUTES.MY_TEAM}
            data-tour="tour-my-team"
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "active" : ""}`
            }
          >
            <MyTeamIcon />
            <span>{t("nav.myTeam", "My Team")}</span>
          </NavLink>

          {/* ─── Leave Tracker Dropdown ─────────────────────────────────────── */}
          <div>
            <button
              type="button"
              aria-expanded={leaveDropdownOpen}
              aria-controls="leave-navigation"
              onClick={() => setLeaveDropdownOpen(!leaveDropdownOpen)}
              data-tour="tour-leave"
              className={`sidebar-link w-full text-left flex items-center ${
                isLeaveActive ? "active" : ""
              }`}
            >
              <LeaveIcon />
              <span className="truncate">{t("nav.leaveTracker")}</span>
              {leaveDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>

            {leaveDropdownOpen && (
              <div
                id="leave-navigation"
                className="mt-1 space-y-0.5 pl-4 transition-all duration-300"
              >
                {leaveLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.to === ROUTES.LEAVE}
                    className={({ isActive }) => subLinkClass(isActive)}
                  >
                    {link.icon}
                    {link.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* ─── Performance Dropdown ───────────────────────────────────────── */}
          <div>
            <button
              type="button"
              aria-expanded={performanceDropdownOpen}
              aria-controls="performance-navigation"
              onClick={() =>
                setPerformanceDropdown({
                  pathname: location.pathname,
                  open: !performanceDropdownOpen,
                })
              }
              data-tour="tour-performance"
              className={`sidebar-link w-full text-left flex items-center ${
                isPerformanceActive ? "active" : ""
              }`}
            >
              <PerformanceIcon />
              <span className="truncate">
                {t("nav.performanceAppraisal")}
              </span>
              {performanceDropdownOpen ? (
                <ChevronUpIcon />
              ) : (
                <ChevronDownIcon />
              )}
            </button>
            {performanceDropdownOpen && (
              <div
                id="performance-navigation"
                className="mt-1 space-y-0.5 pl-4 transition-all duration-300"
              >
                {performanceLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) => subLinkClass(isActive)}
                  >
                    {link.icon}
                    {link.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* ─── Bottom Items (Documents, Settings) ────────────────────────── */}
          <div>
            <button type="button" aria-expanded={documentsDropdownOpen} aria-controls="documents-submenu"
              onClick={() => setDocumentsDropdown({ pathname: location.pathname, open: !documentsDropdownOpen })}
              data-tour="tour-documents"
              className={`sidebar-link w-full text-left flex items-center ${isDocumentsActive ? "active" : ""}`}>
              <DocumentsIcon /><span className="truncate">{t("nav.documents")}</span>
              {documentsDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
            {documentsDropdownOpen && <div id="documents-submenu" className="mt-1 space-y-0.5 pl-4">
              {documentLinks.map(item => <NavLink key={item.to} to={item.to} end={item.to === ROUTES.DOCUMENTS} className={({ isActive }) => subLinkClass(isActive)}>
                {item.icon}{item.label}
              </NavLink>)}
            </div>}
          </div>

          {/* ─── Salary Advance Dropdown ─────────────────────────────────────── */}
          {isSalaryAdvanceVisible && (
            <div>
              <button
                type="button"
                aria-expanded={salaryAdvanceDropdownOpen}
                aria-controls="salary-advance-navigation"
                onClick={() => setSalaryAdvanceDropdown({ pathname: location.pathname, open: !salaryAdvanceDropdownOpen })}
                data-tour="tour-salary-advance"
                className={`sidebar-link w-full text-left flex items-center ${isSalaryAdvanceActive ? "active" : ""}`}
              >
                <SalaryAdvanceIcon />
                <span className="truncate">{t("nav.salaryAdvance", "Salary Advance")}</span>
                {salaryAdvanceDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </button>
              {salaryAdvanceDropdownOpen && (
                <div id="salary-advance-navigation" className="mt-1 space-y-0.5 pl-4 transition-all duration-300">
                  {salaryAdvanceLinks.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === ROUTES.SALARY_ADVANCE}
                      className={({ isActive }) => subLinkClass(isActive)}
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          {navItemsBottom.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === ROUTES.SETTINGS && isPerformanceActive}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Footer */}
      <div className="shrink-0 px-5 py-4 border-t border-white/10 space-y-3">
        <button
          type="button"
          onClick={() => setIsLogoutModalOpen(true)}
          disabled={isLoggingOut}
          className="flex w-full items-center gap-2 rounded-[4px] px-3 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
        >
          <LogoutIcon />
          {isLoggingOut ? "Signing out…" : t("nav.logout")}
        </button>
        {logoutError && <p role="alert" className="text-xs text-red-300">{logoutError}</p>}
      </div>

      <ConfirmDeleteModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={confirmLogout}
        title={t("nav.logout", "Log Out")}
        message="Are you sure you want to sign out of your account?"
        confirmText="Sign out"
        isDeleting={isLoggingOut}
      />
    </aside>
  );
};

export default Sidebar;
